import { defineStore } from "pinia";

export type ActionLoaderPet = "cat" | "dog";

interface StartActionLoaderPayload {
  pet?: ActionLoaderPet;
  messageKey?: string;
}

export default defineStore("action-loader-store", {
  state: () => ({
    activeCount: 0,
    pet: "cat" as ActionLoaderPet,
    messageKey: "alkokh.global.loader.catMessage",
    /** Interpolation for `messageKey` — set by `setMessage`, e.g. `{ done, total }`. */
    messageArgs: null as Record<string, unknown> | null,
  }),

  getters: {
    isVisible: state => state.activeCount > 0,
  },

  actions: {
    start(payload: StartActionLoaderPayload = {}) {
      if (this.activeCount === 0) {
        this.pet = payload.pet ?? "cat";
        this.messageKey =
          payload.messageKey ??
          (this.pet === "dog"
            ? "alkokh.global.loader.dogMessage"
            : "alkokh.global.loader.catMessage");
        this.messageArgs = null;
      }

      this.activeCount += 1;
    },

    /**
     * Replace the message WHILE the loader is up — for batch work that would otherwise sit on
     * "working on it" for ten seconds with no sign of progress (converting five iPhone photos
     * takes 10-15s, and the HEIC decoder serialises on one worker so it cannot be sped up).
     *
     * Only meaningful between a `start` and its `stop`; a later `start` from an idle state
     * resets it. Callers driving a batch should hold the loader open across the whole run so
     * per-request starts don't reset the count.
     */
    setMessage(messageKey: string, messageArgs: Record<string, unknown> | null = null) {
      this.messageKey = messageKey;
      this.messageArgs = messageArgs;
    },

    stop() {
      this.activeCount = Math.max(0, this.activeCount - 1);
      if (this.activeCount === 0)
        this.messageArgs = null;
    },

    reset() {
      this.activeCount = 0;
      this.messageArgs = null;
    },
  },
});
